import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import {
    CheckCircleRounded,
    KeyboardArrowDownRounded,
    RadioButtonUncheckedRounded,
} from '@mui/icons-material';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { HarnessStepInfo, useAndieChatControls } from '@/providers/andie-chat-provider';

const EASE_OUT: [number, number, number, number] = [0.25, 1, 0.5, 1];

const StatusIcon = ({
    status,
    shouldReduceMotion,
}: {
    status: string;
    shouldReduceMotion: boolean | null;
}) => {
    const icon = (() => {
        if (status === 'doing') {
            return (
                <RadioButtonUncheckedRounded sx={{ fontSize: 14, color: 'primary.main', flexShrink: 0 }} />
            );
        }
        if (status === 'done') {
            return <CheckCircleRounded sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />;
        }
        return <RadioButtonUncheckedRounded sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />;
    })();

    if (shouldReduceMotion) {
        return icon;
    }

    return (
        <AnimatePresence mode="wait">
            <motion.div
                key={status}
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.5, opacity: 0 }}
                transition={{ duration: 0.13, ease: EASE_OUT }}
                style={{ display: 'flex', alignItems: 'center' }}
            >
                {icon}
            </motion.div>
        </AnimatePresence>
    );
};

const StepRow = ({
    step,
    index,
    divRef,
}: {
    step: HarnessStepInfo;
    index: number;
    divRef?: React.Ref<HTMLDivElement>;
}) => {
    const shouldReduceMotion = useReducedMotion();
    const status = step.status ?? 'pending';
    const isDone = status === 'done';

    return (
        <motion.div
            ref={divRef}
            layout
            initial={{ opacity: 0, x: shouldReduceMotion ? 0 : -6 }}
            animate={{ opacity: isDone ? 0.45 : 1, x: 0 }}
            transition={{ duration: 0.2, delay: index * 0.04, ease: EASE_OUT }}
        >
            <Stack direction="row" alignItems="center" gap={1} sx={{ py: '5px' }}>
                <StatusIcon status={status} shouldReduceMotion={shouldReduceMotion} />
                <Tooltip enterDelay={400} enterNextDelay={400} title={step.task}>
                    <Typography
                        variant="caption"
                        noWrap
                        sx={{
                            cursor: 'pointer',
                            fontSize: 12,
                            lineHeight: 1.4,
                            color: isDone ? 'text.disabled' : 'text.secondary',
                            flex: 1,
                            fontWeight: status === 'doing' ? 500 : 400,
                        }}
                    >
                        {step.task}
                    </Typography>
                </Tooltip>
            </Stack>
        </motion.div>
    );
};

/**
 * Renders the harness's live plan/todo near the composer: one row per (agent, task) step with a
 * pending/doing/done status, plus an awaiting-confirmation prompt when the run is paused. Shown only
 * for harnessed turns (mode set).
 */
export const AndieHarnessPlan = () => {
    const { t } = useTranslation();
    const { harnessPlan, harnessAwaiting, mode } = useAndieChatControls();
    const [panelVisible, setPanelVisible] = useState(false);
    const [bodyCollapsed, setBodyCollapsed] = useState(true);
    const prevRunIdRef = useRef<string | null>(null);
    const autoHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const shouldReduceMotion = useReducedMotion();

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const doingRef = useRef<HTMLDivElement>(null);

    const steps = harnessPlan?.steps ?? [];
    const doneCount = steps.filter((s) => s.status === 'done').length;
    const completed = harnessPlan?.status === 'completed';
    const allDone = completed || (steps.length > 0 && steps.every((s) => s.status === 'done'));
    const doingStepIndex = steps.find((s) => s.status === 'doing')?.stepIndex ?? null;

    useEffect(() => {
        const prevRunId = prevRunIdRef.current;
        const runId = harnessPlan?.runId ?? null;
        prevRunIdRef.current = runId;

        if (harnessPlan && runId && runId !== prevRunId) {
            setPanelVisible(true);
            setBodyCollapsed(true);
            if (autoHideTimer.current) clearTimeout(autoHideTimer.current);
        }

        if (!harnessPlan) {
            setPanelVisible(false);
            setBodyCollapsed(true);
        }
    }, [harnessPlan]);

    useEffect(() => {
        // Keep the panel open while paused waiting for the user; otherwise auto-hide once complete.
        if (allDone && !harnessAwaiting) {
            autoHideTimer.current = setTimeout(() => setPanelVisible(false), 1500);
        } else if (autoHideTimer.current) {
            clearTimeout(autoHideTimer.current);
        }
        return () => {
            if (autoHideTimer.current) clearTimeout(autoHideTimer.current);
        };
    }, [allDone, harnessAwaiting]);

    useEffect(() => {
        if (doingRef.current) {
            doingRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [doingStepIndex]);

    // The interactive ask-user UI (options + notes) renders separately near the composer when a
    // 'needs-input' pause carries userQuestions. Here we only show the read-only caption fallback for
    // pauses without questions (confirmations, questions, or older/edge 'needs-input' with none).
    // Keep this in lockstep with the render gate in AndieChat, so the caption shows exactly when
    // the interactive UI does not.
    const hasInteractiveOptions =
        harnessAwaiting?.reason === 'needs-input' && !!harnessAwaiting.userQuestions?.length;
    const awaitingText =
        harnessAwaiting && !hasInteractiveOptions
            ? harnessAwaiting.reason === 'needs-input' && harnessAwaiting.userQuestion
                ? harnessAwaiting.userQuestion
                : t(`andie.chat.harness-plan.awaiting.${harnessAwaiting.reason}`)
            : null;

    return (
        <AnimatePresence>
            {mode && panelVisible && steps.length > 1 && (
                <motion.div
                    initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: shouldReduceMotion ? 0 : 4 }}
                    transition={{ duration: 0.25, ease: EASE_OUT }}
                >
                    <Box
                        sx={{
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.paper',
                        }}
                    >
                        {/* Header — always visible, toggles body */}
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            onClick={() => setBodyCollapsed((v) => !v)}
                            sx={{
                                px: 1.5,
                                py: '6px',
                                cursor: 'pointer',
                                userSelect: 'none',
                                transition: 'background-color 150ms',
                                '&:hover': { bgcolor: 'action.hover' },
                            }}
                        >
                            <Stack direction="row" alignItems="center" gap={1}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: 11,
                                        color: 'text.secondary',
                                        fontWeight: 600,
                                        letterSpacing: '0.04em',
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    {t('andie.chat.harness-plan.title')}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: 11, color: 'text.disabled' }}>
                                    {doneCount} / {steps.length}
                                </Typography>
                            </Stack>

                            <motion.div
                                animate={{ rotate: !bodyCollapsed ? 0 : 180 }}
                                transition={{ duration: 0.2 }}
                                style={{ display: 'flex', alignItems: 'center' }}
                            >
                                <KeyboardArrowDownRounded sx={{ fontSize: 16, color: 'text.disabled' }} />
                            </motion.div>
                        </Stack>

                        {/* Collapsible step body */}
                        <AnimatePresence initial={false}>
                            {!bodyCollapsed && (
                                <motion.div
                                    key="body"
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.25, ease: EASE_OUT }}
                                    style={{ overflow: 'hidden' }}
                                >
                                    <Box
                                        ref={scrollContainerRef}
                                        sx={{
                                            maxHeight: 250,
                                            overflowY: 'auto',
                                            '&::-webkit-scrollbar': { width: 4 },
                                            '&::-webkit-scrollbar-thumb': {
                                                borderRadius: 2,
                                                bgcolor: 'text.disabled',
                                            },
                                            '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                                            scrollbarWidth: 'thin',
                                        }}
                                    >
                                        <Stack sx={{ px: 1.5, pb: awaitingText ? 0.5 : 1.5, pt: 0.5 }}>
                                            {steps.map((step, i) => (
                                                <StepRow
                                                    key={step.stepIndex}
                                                    step={step}
                                                    index={i}
                                                    divRef={step.status === 'doing' ? doingRef : undefined}
                                                />
                                            ))}
                                        </Stack>

                                        {awaitingText && (
                                            <Stack sx={{ px: 1.5, pb: 1.5, pt: 0.5 }}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontSize: 11,
                                                        color: 'primary.main',
                                                        fontStyle: 'italic',
                                                    }}
                                                >
                                                    {awaitingText}
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Box>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </Box>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
